import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../services/api';
import { handleApiError, showSuccess } from '../../utils/handleApiError';
import ConfirmModal from '../ConfirmModal';

interface Language {
  id: number;
  code: string;
  name: string;
  native_name: string;
  is_active: boolean;
  is_default: boolean;
}

interface Translation {
  id: number;
  language_id: number;
  group: string;
  key: string;
  value: string;
}

const TranslationManagement: React.FC = () => {
  const { t } = useLanguage();
  const [groups, setGroups] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    loadLanguages();
    loadGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      loadTranslations(selectedGroup);
    }
  }, [selectedGroup]);

  const loadLanguages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/translation-management/languages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setLanguages(data.languages || []);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
      handleApiError(error, 'Hiba történt a nyelvek betöltésekor');
    }
  };

  const loadGroups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/admin/translation-management/groups`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const groupsArray = data.groups || [];
        setGroups(groupsArray);
        if (groupsArray.length > 0 && !selectedGroup) {
          setSelectedGroup(groupsArray[0]);
        }
      } else {
        console.error('Failed to load groups');
        handleApiError({ response: { data: {} } }, 'Hiba történt a fordítási csoportok betöltésekor');
      }
    } catch (error) {
      console.error('Error loading groups:', error);
      handleApiError(error, 'Hiba történt a fordítási csoportok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const loadTranslations = async (group: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/translation-management/groups/${group}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTranslations(Array.isArray(data) ? data : data.translations || []);
      }
    } catch (error) {
      console.error('Error loading translations:', error);
      handleApiError(error, 'Hiba történt a fordítások betöltésekor');
      setTranslations([]);
    }
  };

  const handleDeleteTranslation = async () => {
    if (deleteId === null) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/translation-management/translations/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        showSuccess('Fordítás sikeresen törölve');
        if (selectedGroup) {
          loadTranslations(selectedGroup);
        }
      }
    } catch (error) {
      console.error('Error deleting translation:', error);
      handleApiError(error, 'Hiba történt a fordítás törlésekor');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {t('translation_management', 'admin')}
          </h3>

          {/* Group selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fordítási csoport
            </label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
            >
              <option value="">-- Válasszon csoportot --</option>
              {groups.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
          </div>

          {/* Translations table */}
          {selectedGroup && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kulcs
                    </th>
                    {languages.map((lang) => (
                      <th key={lang.code} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {lang.native_name}
                      </th>
                    ))}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Műveletek
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.keys(Array.isArray(translations) ? translations.reduce((acc: any, t: Translation) => {
                    acc[t.key] = true;
                    return acc;
                  }, {}) : {}).map((key) => (
                    <tr key={key}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {key}
                      </td>
                      {languages.map((lang) => {
                        const translation = Array.isArray(translations) ? translations.find(t => t.key === key && t.language_id === lang.id) : undefined;
                        return (
                          <td key={lang.code} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {translation?.value || '-'}
                          </td>
                        );
                      })}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            const translation = Array.isArray(translations) ? translations.find(t => t.key === key) : undefined;
                            if (translation) {
                              setDeleteId(translation.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Törlés
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!selectedGroup && (
            <div className="text-center py-8">
              <p className="text-gray-500">Válasszon egy fordítási csoportot a megtekintéshez</p>
            </div>
          )}
        </div>
      </div>
      <ConfirmModal
        isOpen={deleteId !== null}
        onConfirm={handleDeleteTranslation}
        onCancel={() => setDeleteId(null)}
        title="Fordítás törlése"
        message="Biztosan törölni szeretné ezt a fordítást?"
        confirmLabel="Törlés"
      />
    </div>
  );
};

export default TranslationManagement;